import { Flame, Snowflake } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatStreak } from '@/lib/achievements'
import { cn } from '@/lib/utils'

interface StreakIndicatorProps {
  currentStreak: number
  longestStreak: number
  lastActivityDate: string | null
  variant?: 'full' | 'compact' | 'badge'
  className?: string
}

export function StreakIndicator({
  currentStreak,
  longestStreak,
  lastActivityDate,
  variant = 'full',
  className,
}: StreakIndicatorProps) {
  const isActive = currentStreak > 0
  const isHot = currentStreak >= 7
  const isBurning = currentStreak >= 30
  
  // Check if streak is at risk (last activity not today)
  const today = new Date().toISOString().split('T')[0]
  const lastDate = lastActivityDate?.split('T')[0]
  const isAtRisk = lastDate && lastDate !== today && currentStreak > 0

  if (variant === 'badge') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={isActive ? 'default' : 'secondary'}
            className={cn(
              'gap-1',
              isBurning && 'bg-orange-500 hover:bg-orange-600',
              isHot && !isBurning && 'bg-amber-500 hover:bg-amber-600',
              className
            )}
          >
            {isActive ? (
              <Flame className={cn('h-3 w-3', isBurning && 'animate-pulse')} />
            ) : (
              <Snowflake className="h-3 w-3" />
            )}
            {currentStreak}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{formatStreak(currentStreak)} streak</p>
          {isAtRisk && <p className="text-warning text-xs">Activity needed today!</p>}
        </TooltipContent>
      </Tooltip>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className={cn(
          'p-2 rounded-full',
          isActive ? 'bg-orange-500/10' : 'bg-muted'
        )}>
          {isActive ? (
            <Flame className={cn(
              'h-5 w-5 text-orange-500',
              isBurning && 'animate-pulse'
            )} />
          ) : (
            <Snowflake className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium">
            {currentStreak > 0 ? `${currentStreak} day streak` : 'No streak'}
          </p>
          {isAtRisk && (
            <p className="text-xs text-warning">Activity needed today!</p>
          )}
        </div>
      </div>
    )
  }

  // Full variant
  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-14 h-14 rounded-full flex items-center justify-center',
            isActive 
              ? 'bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/20' 
              : 'bg-muted'
          )}>
            {isActive ? (
              <Flame className={cn(
                'h-7 w-7 text-white',
                isBurning && 'animate-pulse'
              )} />
            ) : (
              <Snowflake className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
          <div>
            <p className="text-2xl font-bold">{currentStreak}</p>
            <p className="text-sm text-muted-foreground">
              {currentStreak === 1 ? 'Day' : 'Days'} Streak
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-sm font-medium">Best: {longestStreak} days</p>
          {lastActivityDate && (
            <p className="text-xs text-muted-foreground">
              Last: {new Date(lastActivityDate).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      
      {isAtRisk && (
        <div className="flex items-center gap-2 p-2 bg-warning/10 rounded-lg text-warning text-sm">
          <Flame className="h-4 w-4" />
          <span>Make a contribution today to keep your streak alive!</span>
        </div>
      )}
      
      {!isActive && (
        <p className="text-sm text-muted-foreground">
          Start your streak by adding a contribution to any goal!
        </p>
      )}
      
      {isBurning && (
        <div className="flex items-center gap-2 p-2 bg-orange-500/10 rounded-lg text-orange-600 text-sm">
          <Flame className="h-4 w-4 animate-pulse" />
          <span>You're on fire! {currentStreak} days and counting!</span>
        </div>
      )}
    </div>
  )
}
